import React, { memo, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { selectIsBrokerOrSubCompanyUser } from 'store/auth/selectors';

import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import { ContentHeadline } from '../sharedComponents';

import { getMortgageInsuranceConfig } from './MortgageInsurance.config';
import { buildMiCompanyTypeChangeHandler } from './MortgageInsurance.utils';

const MortgageInsurance = ({
  applicationData,
  changedFields,
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  setMultipleInForm,
  className,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
}) => {
  const isBrokerOrSubCompanyUser = useSelector(selectIsBrokerOrSubCompanyUser);
  const miProductType = form.miProductType?.value ?? applicationData?.miProductType;
  const miCompanyType = form.miCompanyType?.value ?? applicationData?.miCompanyType;
  const hasCompanyOtherDescription = !_.isNil(form.miCompanyOtherDescription);

  const onCompanyTypeChange = useMemo(
    () =>
      buildMiCompanyTypeChangeHandler({
        hasCompanyOtherDescription,
        setMultipleInForm,
        validateField,
      }),
    [hasCompanyOtherDescription, setMultipleInForm, validateField],
  );

  const config = useMemo(
    () =>
      getMortgageInsuranceConfig({
        miProductType,
        miCompanyType,
        onCompanyTypeChange,
        storedValues: applicationData,
        isOrderOutcomeReadOnly: isBrokerOrSubCompanyUser,
      }),
    [miProductType, miCompanyType, onCompanyTypeChange, applicationData, isBrokerOrSubCompanyUser],
  );

  useEffect(() => {
    setEntityConfig({
      groups: [...config.groups],
    });
  }, [config, setEntityConfig]);

  return (
    <Box className={className}>
      <ContentHeadline title="Mortgage Insurance" />

      <Box>
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={config}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.MORTGAGE_INSURANCE}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={3}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </Box>
    </Box>
  );
};

MortgageInsurance.propTypes = pagesPropTypes;

export default memo(MortgageInsurance);
